import type { IAction } from '../../action/IAction'
import { ActionArgs } from '../../action/ActionArgs'
import { Field, ID, InputType, ObjectType } from 'type-graphql'

/**
 * Result type for unified Zendesk ticket duplicate detection.
 * Contains normalized information about duplicates from both Order and PurchaseOrder entities.
 */
@ObjectType()
export class ZendeskTicketDuplicateResult {
  /**
   * Entity ID (_id from Order or PurchaseOrder)
   */
  @Field(() => ID)
  _id: string

  /**
   * Entity code (order code or purchase order code)
   */
  @Field(() => String)
  code: string

  /**
   * Creation date of the entity
   */
  @Field(() => Date)
  date: Date

  /**
   * Vendor name (for PurchaseOrder) or Customer name (for Order)
   * Extracted from vendor.profile.name, vendor.company.name, by.profile.name, or by.company.name
   */
  @Field(() => String)
  vendorOrBy: string

  /**
   * Entity type: 'Order' for sales orders, 'PurchaseOrder' for purchase orders
   */
  @Field(() => String)
  type: 'Order' | 'PurchaseOrder'

  /**
   * Entity status
   */
  @Field(() => String)
  status: string
}

/**
 * Args for checking if a Zendesk ticket number is already used in other orders or purchase orders.
 * This unified action helps identify duplicate ticket assignments across both entity types.
 */
@InputType()
export class CheckZendeskTicketDuplicatesArgs extends ActionArgs {
  /**
   * The Zendesk ticket number to check for duplicates (numeric value only, e.g., "4570")
   */
  @Field(() => String)
  ticketNo: string

  /**
   * Optional order ID to exclude from the duplicate check (to exclude current order being edited)
   */
  @Field(() => ID, { nullable: true })
  excludeOrderId?: string

  /**
   * Optional purchase order ID to exclude from the duplicate check (to exclude current purchase order being edited)
   */
  @Field(() => ID, { nullable: true })
  excludePurchaseOrderId?: string
}

/**
 * Interface for the CheckZendeskTicketDuplicates action
 * Returns an array of ZendeskTicketDuplicateResult that includes duplicates from both Order and PurchaseOrder
 */
export interface ICheckZendeskTicketDuplicates
  extends IAction<ZendeskTicketDuplicateResult[], CheckZendeskTicketDuplicatesArgs> {}
